/**
 * PDT (Position Designation Tool) freshness rules.
 *
 * - VALID: uploaded within the last 9 months
 * - NEEDS_UPDATE: older than 9 months but less than 12 months
 * - EXPIRED: 12 months or older
 */

/** Months after upload when PDT is still valid. */
export const PDT_VALID_WITHIN_MONTHS = 9;

/** Months after upload when PDT is expired. */
export const PDT_EXPIRED_AFTER_MONTHS = 12;

export enum PdtFreshnessStatus {
  EXPIRED = 'EXPIRED',
  NEEDS_UPDATE = 'NEEDS_UPDATE',
  VALID = 'VALID',
}

/**
 * Subtract calendar months from a date (mutates a copy only).
 */
export function subtractMonths(from: Date, months: number): Date {
  const result = new Date(from.getTime());
  result.setMonth(result.getMonth() - months);

  return result;
}

/**
 * Add calendar months to a date (mutates a copy only).
 */
export function addMonths(from: Date, months: number): Date {
  const result = new Date(from.getTime());
  result.setMonth(result.getMonth() + months);

  return result;
}

/**
 * Date when the PDT expires (upload date + {@link PDT_EXPIRED_AFTER_MONTHS}).
 */
export function getPdtExpirationDate(pdtUploadedAt: Date | string | null | undefined): Date | null {
  const uploaded = pdtUploadedAt == null ? null : parsePdtUploadedAt(pdtUploadedAt);

  if (!uploaded) {
    return null;
  }

  return addMonths(uploaded, PDT_EXPIRED_AFTER_MONTHS);
}

/**
 * Parse upload timestamp for freshness checks.
 */
export function parsePdtUploadedAt(pdtUploadedAt: Date | string): Date | null {
  const uploaded = pdtUploadedAt instanceof Date ? pdtUploadedAt : new Date(pdtUploadedAt);

  if (Number.isNaN(uploaded.getTime())) {
    return null;
  }

  return uploaded;
}

/**
 * Classify PDT upload age relative to policy thresholds.
 *
 * @param pdtUploadedAt - When the PDT was uploaded (ISO string or Date)
 * @param referenceDate - Evaluation date (defaults to now; inject in tests)
 * @returns Freshness status, or null when upload date is missing/invalid
 */
export function getPdtFreshnessStatus(
  pdtUploadedAt: Date | string | null | undefined,
  referenceDate: Date = new Date(),
): PdtFreshnessStatus | null {
  if (pdtUploadedAt == null) {
    return null;
  }

  const uploaded = parsePdtUploadedAt(pdtUploadedAt);
  if (!uploaded) {
    return null;
  }

  const validAfter = subtractMonths(referenceDate, PDT_VALID_WITHIN_MONTHS);
  const expiredAfter = subtractMonths(referenceDate, PDT_EXPIRED_AFTER_MONTHS);

  if (uploaded > validAfter) {
    return PdtFreshnessStatus.VALID;
  }

  if (uploaded > expiredAfter) {
    return PdtFreshnessStatus.NEEDS_UPDATE;
  }

  return PdtFreshnessStatus.EXPIRED;
}
